import { useState } from 'react';
import { UsageWithPercentage } from '@/types/subscription';
import { useLanguage } from '@/contexts';
import { FeatureKey } from '@/types/subscription';

interface FloatingUsageWidgetProps {
  usage: UsageWithPercentage[];
}

/**
 * FloatingUsageWidget
 * Expandable floating widget showing top 5 usage features
 * Positioned in the bottom-right corner
 */
export function FloatingUsageWidget({ usage }: FloatingUsageWidgetProps) {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);

  // Take only top 5
  const topFive = usage.slice(0, 5);

  if (topFive.length === 0) {
    return null;
  }

  const getColorClasses = (percentage: number) => {
    if (percentage >= 90) {
      return {
        bg: 'bg-red-500 dark:bg-red-400',
        text: 'text-red-700 dark:text-red-300',
        lightBg: 'bg-red-50 dark:bg-red-900/30',
        border: 'border-red-200 dark:border-red-700'
      };
    }
    if (percentage >= 75) {
      return {
        bg: 'bg-yellow-500 dark:bg-yellow-400',
        text: 'text-yellow-700 dark:text-yellow-300',
        lightBg: 'bg-yellow-50 dark:bg-yellow-900/30',
        border: 'border-yellow-200 dark:border-yellow-700'
      };
    }
    return {
      bg: 'bg-green-500 dark:bg-green-400',
      text: 'text-green-700 dark:text-green-300',
      lightBg: 'bg-green-50 dark:bg-green-900/30',
      border: 'border-green-200 dark:border-green-700'
    };
  };

  const formatFeatureKey = (key: FeatureKey): string => {
    return t(`usageWidget.features.${key}`);
  };

  // Get highest usage for collapsed state
  const highest = topFive[0];
  const highestColors = getColorClasses(highest.percentage_used || 0);

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {isExpanded ? (
        // Expanded view - shows all top 5
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl dark:shadow-2xl dark:shadow-slate-900/50 border border-gray-200 dark:border-slate-700 w-80 max-h-96 overflow-hidden transition-colors duration-300">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 transition-colors duration-300">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-100 uppercase tracking-wide transition-colors duration-300">
              {t('usageWidget.title')}
            </h3>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors duration-300"
              aria-label={t('usageWidget.tooltips.collapse')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
            {topFive.map((item) => {
              const colors = getColorClasses(item.percentage_used || 0);
              return (
                <div
                  key={item.feature_key}
                  className={`relative flex items-center justify-between p-3 rounded-lg border ${colors.border} ${colors.lightBg} transition-all duration-200`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-2 h-2 rounded-full ${colors.bg} animate-pulse`}></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-600 dark:text-slate-300 truncate transition-colors duration-300">
                        {formatFeatureKey(item.feature_key)}
                      </div>
                      <div className={`text-sm font-semibold ${colors.text} transition-colors duration-300`}>
                        {t('usageWidget.status.limited')
                          .replace('{{current}}', String(item.current_usage))
                          .replace('{{limit}}', String(item.limit_value))}
                      </div>
                    </div>
                  </div>
                  <div className={`text-xs font-bold ${colors.text} ml-2 transition-colors duration-300`}>
                    {t('usageWidget.status.percentage').replace('{{percentage}}', String(item.percentage_used))}
                  </div>
                  {/* Progress bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-slate-600 rounded-b-lg overflow-hidden transition-colors duration-300">
                    <div
                      className={`h-full ${colors.bg} transition-all duration-500`}
                      style={{ width: `${Math.min(item.percentage_used || 0, 100)}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        // Collapsed view - shows only highest usage with percentage
        <button
          onClick={() => setIsExpanded(true)}
          className={`flex items-center gap-3 px-4 py-3 rounded-full shadow-lg border-2 ${highestColors.border} ${highestColors.lightBg} hover:shadow-xl transition-all duration-200 group dark:hover:shadow-2xl dark:hover:shadow-slate-900/40`}
          aria-label={t('usageWidget.tooltips.expand')}
        >
          <div className={`w-3 h-3 rounded-full ${highestColors.bg} animate-pulse`}></div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-700 dark:text-slate-200 transition-colors duration-300">
              {formatFeatureKey(highest.feature_key)}
            </span>
            <span className={`text-sm font-bold ${highestColors.text} transition-colors duration-300`}>
              {t('usageWidget.status.percentage').replace('{{percentage}}', String(highest.percentage_used))}
            </span>
          </div>
          <svg className="w-4 h-4 text-gray-400 dark:text-slate-500 group-hover:text-gray-600 dark:group-hover:text-slate-300 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}
    </div>
  );
}

